import type { Command } from "prosemirror-state";

import { nodes } from "../schema";
import type { MentionSearchResult, UploadResult } from "../types";

export interface ImageOptions extends UploadResult {
  alt?: string | null;
  layoutClass?: string | null;
}

export function insertImage(options: ImageOptions): Command {
  return (state, dispatch) => {
    const node = nodes.image.create({
      src: options.url,
      alt: options.alt ?? null,
      layoutClass: options.layoutClass ?? null,
      ...(options.width === undefined ? {} : { width: options.width }),
      ...(options.height === undefined ? {} : { height: options.height }),
    });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    return true;
  };
}

export function insertVideo(options: UploadResult & { title?: string }): Command {
  return (state, dispatch) => {
    const node = nodes.video.create({
      src: options.url,
      title: options.title ?? null,
      width: options.width ?? null,
      height: options.height ?? null,
    });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node).scrollIntoView());
    return true;
  };
}

export function insertAttachment(options: {
  url: string;
  title: string;
  size?: number;
  contentType?: string | null;
  preview?: boolean;
}): Command {
  return (state, dispatch) => {
    const node = nodes.attachment.create({
      href: options.url,
      title: options.title,
      // `size` is a string in the model: an attachment can exceed 2^53 bytes.
      size: String(options.size ?? 0),
      contentType: options.contentType ?? null,
      preview: options.preview ?? false,
    });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node).scrollIntoView());
    return true;
  };
}

export function insertEmbed(href: string): Command {
  return (state, dispatch) => {
    const node = nodes.embed.create({ href });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node).scrollIntoView());
    return true;
  };
}

export function insertMention(result: MentionSearchResult): Command {
  return (state, dispatch) => {
    const node = nodes.mention.create({
      type: result.type,
      label: result.label,
      modelId: result.modelId,
      id: crypto.randomUUID(),
      ...(result.href === undefined ? {} : { href: result.href }),
    });
    if (dispatch) {
      dispatch(state.tr.replaceSelectionWith(node, false).insertText(" ").scrollIntoView());
    }
    return true;
  };
}

export function insertEmoji(name: string): Command {
  return (state, dispatch) => {
    const node = nodes.emoji.create({ "data-name": name });
    if (dispatch) dispatch(state.tr.replaceSelectionWith(node, false).scrollIntoView());
    return true;
  };
}

/**
 * Embeds render an `iframe`, so the URL is checked against an allowlist of
 * hosts rather than trusted because it happens to be in the document: a
 * document is shared content, and an arbitrary framed origin is an XSS vector
 * dressed as a feature. Anything not listed renders as a link instead.
 */
export const EMBED_HOSTS: readonly string[] = [
  "airtable.com",
  "codepen.io",
  "codesandbox.io",
  "descript.com",
  "figma.com",
  "gist.github.com",
  "docs.google.com",
  "drive.google.com",
  "grafana.com",
  "instagram.com",
  "linear.app",
  "loom.com",
  "lucid.app",
  "marvelapp.com",
  "mermaid.live",
  "miro.com",
  "observablehq.com",
  "player.vimeo.com",
  "prezi.com",
  "soundcloud.com",
  "spotify.com",
  "open.spotify.com",
  "trello.com",
  "typeform.com",
  "vimeo.com",
  "whimsical.com",
  "youtube.com",
  "youtu.be",
  "youtube-nocookie.com",
];

function hostAllowed(host: string): boolean {
  const lower = host.toLowerCase().replace(/^www\./, "");
  return EMBED_HOSTS.some((allowed) => lower === allowed || lower.endsWith(`.${allowed}`));
}

export function isEmbeddableUrl(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === "https:" && hostAllowed(url.hostname);
  } catch {
    return false;
  }
}

/**
 * The `src` an allowed URL is framed with. Share URLs that are not themselves
 * framable get their documented embed form; everything else is framed as-is.
 */
export function embedSource(href: string): string | null {
  if (!isEmbeddableUrl(href)) return null;
  const url = new URL(href);
  const host = url.hostname.toLowerCase().replace(/^www\./, "");
  if (host === "youtu.be") {
    return `https://www.youtube.com/embed/${url.pathname.replace(/^\//, "")}`;
  }
  if (host === "youtube.com" || host === "youtube-nocookie.com") {
    const id = url.searchParams.get("v");
    if (id !== null) return `https://www.youtube.com/embed/${id}`;
  }
  if (host === "vimeo.com") {
    const id = /^\/(\d+)/.exec(url.pathname)?.[1];
    if (id !== undefined) return `https://player.vimeo.com/video/${id}`;
  }
  return url.toString();
}
